import "../styles/dashboard.css";
import ClientTreemap from "../components/ClientTreemap";
import ClientTreemapTable from "../components/ClientTreemapTable";
import ConsultantClientTable from "../components/ConsultantClientTable";
import PerformancePanel from "../components/PerformancePanel";
import PullFromChronaModal from "../components/PullFromChronaModal";
import { useMemo, useState, useEffect } from "react";
import { calculateMargins } from "../utils/calculateMargins";
import { calculateConsultantClientTable } from "../utils/calculateConsultantClientTable";
import TimeNavigator from "../components/TimeNavigator";
import { getPeriodRange } from "../utils/periodRange";
import { filterHoursByPeriod } from "../utils/filterHoursByPeriod";
import { getOverheadForPeriod } from "../utils/getOverheadForPeriod";
import { computeFixedRevenue } from "../utils/computeFixedRevenue";
import { API_BASE_URL } from "../config";
import logo from "../assets/logoEU.png";

// Single source of truth for the localStorage keys. The restore effect, the
// upload handlers, the Chrona pass-through and the Reset button must all use
// exactly these names — drift between writer and reader silently resurrects
// "deleted" data (audit #15/#19/#31).
const STORAGE_KEYS = {
  internals: "orka_internals",
  externals: "orka_externals",
  rates: "orka_rates",
};

// Shape version of what we persist. BUMP THIS whenever a stored blob's
// meaning changes — a renamed field, a new counter the calc relies on, a
// different alias convention upstream. Blobs stamped with anything else are
// dropped on mount instead of being fed to a calc that expects the new
// shape: safeParse only ever caught malformed JSON, and a well-formed blob
// from an older build reads as valid while quietly producing wrong numbers.
// That is how a pre-rename cache kept "Lhoist SA" alive as a phantom client
// long after the feed stopped emitting it.
//
// 1 -> 2 (2026-08-07): rows carry hoursMissingCosts and no longer carry
//        hoursWithRates; pulls stamp their source and time.
const SCHEMA_VERSION = 2;

// Parse a cached localStorage value defensively: corrupt/truncated JSON must
// not crash the whole Dashboard on mount. On failure we drop the bad key and
// continue with null (audit #20/#30). An unstamped or differently-stamped
// blob is treated the same way — dropped, not migrated: re-pulling is one
// click and always correct, whereas a migration guesses.
const safeParse = (key) => {
  try {
    const v = localStorage.getItem(key);
    if (!v) return null;
    const parsed = JSON.parse(v);
    if (parsed && parsed.__schema !== SCHEMA_VERSION) {
      console.warn(
        `Cached ${key} was written by an older version of Margix ` +
          `(schema ${parsed.__schema ?? "none"} vs ${SCHEMA_VERSION}); ` +
          `dropping it. Import or pull again.`
      );
      localStorage.removeItem(key);
      return null;
    }
    return parsed;
  } catch (e) {
    console.error(`Corrupt cache for ${key}, clearing it.`, e);
    localStorage.removeItem(key);
    return null;
  }
};

// Single writer for everything that lands in localStorage, so no site can
// forget the stamp. `meta` records WHERE the data came from and WHEN — the
// dashboard had no way to distinguish data pulled minutes ago from data
// pulled months ago, and every "this doesn't add up" so far has traced back
// to that.
const persist = (key, data, meta) => {
  const stamped = { ...data, __schema: SCHEMA_VERSION, ...meta };
  localStorage.setItem(key, JSON.stringify(stamped));
  return stamped;
};

const stampNow = (source) => ({
  __source: source,
  __pulledAt: new Date().toISOString(),
});

// "3 minutes ago" / "2 days ago" for the freshness chips.
function timeAgo(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return null;
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  return days === 1 ? "yesterday" : `${days} days ago`;
}

// Amber, dismissible list of notes a pull reported. Renders nothing when
// there are none, so callers can drop it in unconditionally.
function PullNotices({ title, notes, onDismiss }) {
  if (!notes || notes.length === 0) return null;
  return (
    <div
      role="alert"
      style={{
        margin: "0 24px 16px",
        padding: "12px 16px",
        borderRadius: 8,
        background: "#fff8ec",
        border: "1px solid #f3d8a8",
        color: "#7a5a1e",
        fontSize: 13,
        lineHeight: 1.5,
      }}
    >
      <div
        style={{
          fontWeight: 700,
          marginBottom: 6,
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <span>⚠ {title}</span>
        <button
          onClick={onDismiss}
          style={{
            background: "transparent",
            border: "none",
            color: "#7a5a1e",
            cursor: "pointer",
            fontSize: 16,
            lineHeight: 1,
          }}
          aria-label="Dismiss notes"
        >
          ×
        </button>
      </div>
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        {notes.map((n, i) => (
          <li key={i} style={{ marginBottom: 2 }}>{n}</li>
        ))}
      </ul>
    </div>
  );
}

const normText = (s) => String(s ?? "").trim();
const normProject = (s) =>
  normText(s)
    .toUpperCase()
    .replace(/\(.*?\)/g, "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export default function Dashboard() {

  const [internals, setInternals] = useState(null);
  const [externals, setExternals] = useState(null);
  const [rates, setRates] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [pullChronaOpen, setPullChronaOpen] = useState(false);
  // Inline upload-error banner state (replaces blocking alert(), audit #46).
  const [uploadError, setUploadError] = useState(null);
  // Resolution notes from the last Sourcix rates pull (unmatched name
  // spellings etc.) — informative, not errors, so they get their own
  // dismissible amber banner instead of the red one.
  const [sourcixNotices, setSourcixNotices] = useState([]);
  // Same, for the Talexa cost pull (missing Rates names, leavers with no
  // end date — data problems that would otherwise shift the numbers
  // quietly).
  const [talexaNotices, setTalexaNotices] = useState([]);
  // The token comes from the Margix login pass-through — null/empty when
  // the user has no access_chrona or Chrona was unreachable at login. Used
  // to gate the "Pull from Chrona" button. Held in state and kept in sync
  // with the storage so it never goes stale (e.g. logout in another tab),
  // audit #45.
  const [hasChronaToken, setHasChronaToken] = useState(
    () => !!localStorage.getItem("chrona_token")
  );

  /* -------------------------------
     RESTORE DATA FROM LOCAL STORAGE
  -------------------------------- */

  useEffect(() => {

    const savedInternals = safeParse(STORAGE_KEYS.internals);
    const savedExternals = safeParse(STORAGE_KEYS.externals);
    const savedRates = safeParse(STORAGE_KEYS.rates);

    if (savedInternals) setInternals(savedInternals);
    if (savedExternals) setExternals(savedExternals);
    if (savedRates) setRates(savedRates);

    // Both merges persist their notes inside the blob, but nothing ever
    // read them back: a reload restored the problem data and lost every
    // note describing the problem.
    if (savedRates?.warnings) {
      setSourcixNotices(savedRates.warnings.sourcix || []);
      setTalexaNotices(savedRates.warnings.talexa || []);
    }

  }, []);

  // Keep the Chrona-token gate in sync if the token is cleared/refreshed in
  // another tab (storage events only fire cross-document), audit #45.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "chrona_token" || e.key === null) {
        setHasChronaToken(!!localStorage.getItem("chrona_token"));
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // seguridad: si cambian los datos cargados, resetea drilldown
  useEffect(() => {
    setSelectedClient(null);
  }, [internals, externals, rates]);

  // ------------------------------
  // TIME NAVIGATOR
  // ------------------------------

  // Open on the CURRENT month so the dashboard always lands on live data.
  // This used to be hardcoded to the full year 2025, which went staler every
  // month and made everyone navigate before seeing anything useful.
  const [view, setView] = useState("month");
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [periodIndex, setPeriodIndex] = useState(() => new Date().getMonth());

  const { periodFrom, periodTo } = useMemo(
    () => getPeriodRange(view, year, periodIndex),
    [view, year, periodIndex]
  );

  // The rates workbook REPLACES the whole rates blob — selling, buying and
  // overhead — while both pulls only merge. Since the feeds now carry the
  // bulk of the data, one click here can silently swap a fully pulled set
  // for whatever the file holds, and nothing on screen would say so: the
  // chip stays green either way. Ask first, but only when there is a pull
  // to lose, so the plain first import stays a single click.
  function handleRatesExcelClick() {
    const pulled = rates?.warnings?.sourcix || rates?.warnings?.talexa;
    if (
      ratesOk &&
      pulled &&
      // eslint-disable-next-line no-alert
      !window.confirm(
        "This replaces ALL loaded rates, costs and overhead with the " +
          "workbook — including what you pulled from Sourcix and Talexa.\n\n" +
          "Pull both again afterwards to get the app data back.\n\nContinue?"
      )
    ) {
      return;
    }
    document.getElementById("excel-rates")?.click();
  }

  function handleChronaLoaded(role, data) {
    // Chrona's xlsx is built by the same shared `procesar_horas_excel` so
    // the response shape matches Margix's manual-upload result. We just
    // route the data into the right slot and persist it like uploadExcel
    // would.
    const stamp = stampNow("Chrona");
    if (role === "internals") {
      setInternals(persist(STORAGE_KEYS.internals, data, stamp));
    } else if (role === "externals") {
      setExternals(persist(STORAGE_KEYS.externals, data, stamp));
    }
    // A successful pull means whatever the last failure was is no longer
    // the current state of things; leaving the red banner up made a fixed
    // problem look live.
    setUploadError(null);
  }

  function handleResetData() {
    // Remove the EXACT keys the data is persisted under (orka_*), otherwise
    // the restore effect resurrects the "deleted" data on next load
    // (audit #15/#19/#31).
    localStorage.removeItem(STORAGE_KEYS.internals);
    localStorage.removeItem(STORAGE_KEYS.externals);
    localStorage.removeItem(STORAGE_KEYS.rates);

    setInternals(null);
    setExternals(null);
    setRates(null);

    setSelectedClient(null);
    setHoveredItem(null);
    setUploadError(null);
    setSourcixNotices([]);
    setTalexaNotices([]);
  }

  /* -------------------------------
     PULL RATES FROM SOURCIX
  -------------------------------- */

  // Replace the rows a pull actually carries and keep everything else. A
  // straight replace would wipe whatever the other source holds — no error,
  // just a silently zeroed half of the margin model.
  //
  // Each SHEET is scoped by its OWN user set. Deriving one set from both
  // sheets and applying it to both, as the Sourcix merge used to, evicts a
  // person's selling rate because the pull happened to carry their buying
  // cost — so a Sourcix pull could drop a Talexa-supplied selling rate and
  // put nothing back, and the only defence was remembering to always pull
  // Talexa last. Per-sheet scoping removes that ordering rule entirely.
  function mergeRates(prev, pulled, { overhead }) {
    if (!prev) return pulled;
    const usersOf = (rows) => new Set((rows || []).map((r) => normText(r.User)));
    const sellUsers = usersOf(pulled.selling_rates);
    const costUsers = usersOf(pulled.buying_costs);
    return {
      ...prev,
      selling_rates: [
        ...(prev.selling_rates || []).filter((r) => !sellUsers.has(normText(r.User))),
        ...(pulled.selling_rates || []),
      ],
      buying_costs: [
        ...(prev.buying_costs || []).filter((r) => !costUsers.has(normText(r.User))),
        ...(pulled.buying_costs || []),
      ],
      // Only Talexa owns overhead — it is internal cost. Sourcix carries
      // none, so its pull must keep whatever is already loaded rather than
      // blanking it.
      overhead: overhead === "keep" ? prev.overhead || [] : pulled.overhead || [],
      warnings: { ...(prev.warnings || {}), ...(pulled.warnings || {}) },
    };
  }

  const mergeSourcixRates = (prev, pulled) =>
    mergeRates(prev, pulled, { overhead: "keep" });

  // Talexa carries the INTERNAL people's cost, their selling rates and the
  // overhead — the last of which it owns outright.
  const mergeTalexaCosts = (prev, pulled) =>
    mergeRates(prev, pulled, { overhead: "replace" });

  // Both rate pulls do the same six things; only the endpoint, the merge
  // rule and which notice slot they fill differ.
  async function pullRates({ endpoint, source, merge, setNotices, noticeKey }) {
    setLoading(true);
    setUploadError(null);

    try {
      const res = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setUploadError(data.detail || `${source} pull failed (${res.status})`);
        return;
      }
      // A 200 whose body is not a feed — a proxy's HTML error page, a
      // truncated response — parsed to {} above and then merged as "a pull
      // carrying nobody", which for Talexa meant replacing the overhead
      // with []. Silently, and the overhead banner is suppressed while
      // rates look loaded. Refuse the response instead.
      if (!Array.isArray(data.buying_costs) || !Array.isArray(data.selling_rates)) {
        setUploadError(
          `${source} returned an unexpected response. Nothing was changed — try again.`
        );
        return;
      }

      // Merge against what is in STORAGE, not the state this handler closed
      // over: another tab (or a Reset that happened mid-flight) may have
      // moved on since the fetch started, and merging into a stale snapshot
      // silently reinstates it.
      const current = safeParse(STORAGE_KEYS.rates) ?? rates;
      const merged = persist(
        STORAGE_KEYS.rates,
        merge(current, data),
        stampNow(source)
      );
      setRates(merged);
      setNotices(data.warnings?.[noticeKey] || []);
    } catch (err) {
      console.error(`${source} pull failed:`, err);
      setUploadError(err.message || `Error pulling from ${source}.`);
    } finally {
      setLoading(false);
    }
  }

  const handlePullTalexaCosts = () =>
    pullRates({
      endpoint: "/excel/import/costs-from-talexa",
      source: "Talexa",
      merge: mergeTalexaCosts,
      setNotices: setTalexaNotices,
      noticeKey: "talexa",
    });

  const handlePullSourcixRates = () =>
    pullRates({
      endpoint: "/excel/import/rates-from-sourcix",
      source: "Sourcix",
      merge: mergeSourcixRates,
      setNotices: setSourcixNotices,
      noticeKey: "sourcix",
    });

  const sellingRates = rates?.selling_rates || [];
  const buyingCosts = rates?.buying_costs || [];
  const overhead = rates?.overhead || null;

  const scaledOverhead = useMemo(
    () => getOverheadForPeriod(overhead, periodFrom, periodTo),
    [overhead, periodFrom, periodTo]
  );

  // Detect fixed-rate rows that overlap in time for the same (consultant,
  // client) within the selected period. An overlap means two fixed rates apply
  // to the same day — the revenue silently uses the one with the latest start
  // date, which is almost always a date-entry mistake in the rates Excel (e.g.
  // a part-time period hidden under a full-time one). Surface it so it is never
  // silently wrong again.
  const fixedOverlaps = useMemo(() => {
    if (!sellingRates.length) return [];
    return (
      computeFixedRevenue(sellingRates, periodFrom, periodTo).fixedOverlaps || []
    );
  }, [sellingRates, periodFrom, periodTo]);

  const internalsOk = (internals?.rows?.length || 0) > 0;
  const externalsOk = (externals?.rows?.length || 0) > 0;
  const ratesOk = sellingRates.length > 0 && buyingCosts.length > 0;

  const [showInfo, setShowInfo] = useState(false);

  /* -------------------------------
     UPLOAD EXCEL
  -------------------------------- */

  async function uploadExcel({ file, endpoint, onSuccess, storageKey, source }) {

    const formData = new FormData();
    formData.append("file", file);

    setLoading(true);
    setUploadError(null);

    try {

      const res = await fetch(endpoint, {
        method: "POST",
        // The /excel/* endpoints now require a valid Margix JWT.
        headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
        body: formData,
      });

      // Check res.ok BEFORE parsing the success body. A non-JSON error body
      // (502/504 HTML from the proxy, gateway timeout, oversized file rejected
      // at the edge) must not be swallowed as a generic message (audit #22/#29).
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setUploadError(data.detail || `Upload failed (${res.status})`);
        return;
      }

      const data = await res.json();

      // Stamp on the way in so state and storage hold the SAME object —
      // otherwise a reload would show a "loaded at" the screen never did.
      onSuccess(persist(storageKey, data, stampNow(source)));

    } catch (err) {
      console.error("Excel upload failed:", err);
      setUploadError(err.message || "Error uploading file.");
    } finally {
      setLoading(false);
    }
  }

  /* ---------------- CLIENTS ---------------- */

  const clientMargins = useMemo(() => {

    const internalRows = (internals?.rows || []).map((r) => ({
      ...r,
      User: normText(r.User),
      Project: normProject(r.Project),
      __type: "internal",
    }));

    const externalRows = (externals?.rows || []).map((r) => ({
      ...r,
      User: normText(r.User),
      Project: normProject(r.Project),
      __type: "external",
    }));

    const filteredInternal = filterHoursByPeriod(internalRows, periodFrom, periodTo);
    const filteredExternal = filterHoursByPeriod(externalRows, periodFrom, periodTo);
    const hoursData = [...filteredInternal, ...filteredExternal];

    const ready =
      hoursData.length > 0 && sellingRates.length > 0 && buyingCosts.length > 0;

    if (!ready) return [];

    return calculateMargins({
      hoursData,
      sellingRates,
      buyingCosts,
      overhead: scaledOverhead,
      periodFrom,
      periodTo,
    });

  }, [internals, externals, sellingRates, buyingCosts, overhead, periodFrom, periodTo, scaledOverhead]);

  /* CONSULTANT × CLIENT */

  const consultantClientRows = useMemo(() => {

    const internalRows = (internals?.rows || []).map((r) => ({
      ...r,
      User: normText(r.User),
      Project: normProject(r.Project),
      __type: "internal",
    }));

    const externalRows = (externals?.rows || []).map((r) => ({
      ...r,
      User: normText(r.User),
      Project: normProject(r.Project),
      __type: "external",
    }));

    const filteredInternal = filterHoursByPeriod(internalRows, periodFrom, periodTo);
    const filteredExternal = filterHoursByPeriod(externalRows, periodFrom, periodTo);
    const hoursData = [...filteredInternal, ...filteredExternal];

    if (
      hoursData.length === 0 ||
      sellingRates.length === 0 ||
      buyingCosts.length === 0
    )
      return [];

    return calculateConsultantClientTable({
      hoursData,
      sellingRates,
      buyingCosts,
      overhead: scaledOverhead,
      periodFrom,
      periodTo,
    });

  }, [internals, externals, sellingRates, buyingCosts, overhead, periodFrom, periodTo, scaledOverhead]);

  /* TREEMAP SWITCH */

  const treemapData = useMemo(() => {

    // Copy before sorting: .sort mutates, and clientMargins is a memo the
    // client table renders from too — its row order was a side effect of
    // whether this memo happened to run.
    if (!selectedClient)
      return [...clientMargins].sort((a,b)=> b.revenue - a.revenue);

    return consultantClientRows
      .filter((r) => r.project === selectedClient)
      .map((r) => ({
        name: r.user,
        hours: r.hours,
        revenue: r.revenue,
        // Carry the per-consultant overhead into the drill-down treemap so the
        // shared ClientTreemap tooltip shows it here too (it already shows it at
        // the client/root level). Same value the right-side table uses
        // (treemapTableData -> r.overhead); external consultants get 0.
        overhead: r.overhead,
        margin: r.margin,
      }))
      .sort((a,b)=> b.revenue - a.revenue);

  }, [selectedClient, clientMargins, consultantClientRows]);

  const treemapTableData = useMemo(() => {

    if (!selectedClient) return clientMargins;

    return consultantClientRows
      .filter((r) => r.project === selectedClient)
      .map((r) => ({
        name: r.user,
        hours: r.hours,
        revenue: r.revenue,
        directCost: r.directCost,
        overhead: r.overhead,
        cost: r.directCost + r.overhead,
        margin: r.margin,
        // Both warning counters have to survive this remap. The client row
        // shows a ⚠, the user clicks it to find out WHO — and until now
        // this map dropped the fields, so the drill-down came up clean and
        // the question the icon raised had no answer anywhere.
        hoursMissingRates: r.hoursMissingRates,
        hoursMissingCosts: r.hoursMissingCosts,
      }));

  }, [selectedClient, clientMargins, consultantClientRows]);

  const importsOk = internalsOk && externalsOk && ratesOk;

  // Freshness of each slot. Hours take the OLDER of internals/externals —
  // the honest figure, since a fresh internals pull says nothing about
  // externals loaded weeks earlier.
  const loadedAt = useMemo(() => {
    const describe = (...blobs) => {
      const stamped = blobs.filter((b) => b && b.__pulledAt);
      if (!stamped.length) return null;
      const oldest = stamped.reduce((a, b) =>
        new Date(a.__pulledAt) <= new Date(b.__pulledAt) ? a : b
      );
      const sources = [...new Set(stamped.map((b) => b.__source))];
      return {
        source: sources.join(" + "),
        ago: timeAgo(oldest.__pulledAt),
        exact: new Date(oldest.__pulledAt).toLocaleString(),
      };
    };
    return {
      hours: describe(internals, externals),
      rates: describe(rates),
    };
  }, [internals, externals, rates]);

  // Unpriced hours across every client in the period, for the banner below.
  // Summed from the same rows the client table renders, so the banner can
  // never disagree with what a reader counts by hand.
  const gaps = useMemo(
    () =>
      clientMargins.reduce(
        (acc, c) => ({
          rates: acc.rates + (c.hoursMissingRates || 0),
          costs: acc.costs + (c.hoursMissingCosts || 0),
          hours: acc.hours + (c.hours || 0),
        }),
        { rates: 0, costs: 0, hours: 0 }
      ),
    [clientMargins]
  );
  const fmtHours = (h) =>
    Number(h || 0).toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    });
  const pctOfHours = (h) =>
    gaps.hours > 0 ? `${((h / gaps.hours) * 100).toFixed(1)}%` : "—";

return (
    <div className="dashboard-container">

      {/* ── HEADER ── */}
      <div className="dash-header">
        <div
          className="dash-header__brand"
          onMouseEnter={() => setShowInfo(true)}
          onMouseLeave={() => setShowInfo(false)}
        >
          <img src={logo} alt="Orka Logo" className="dash-header__logo" />
          <div>
            <h1 className="dash-header__title">Client Profitability Dashboard</h1>
            <p className="dash-header__subtitle">Consulting Performance · Orka</p>
          </div>

          {showInfo && (
            <div className="dash-info-tooltip">
              <strong>Margin Calculation</strong><br /><br />
              The margin is calculated based on the hourly cost per consultant compared to the sales price charged to the client.<br /><br />
              For external consultants, the calculation is straightforward:<br /><br />
              <strong>Margin (EUR) = (Sales price – Purchase price) × Sold hours</strong><br /><br />
              For internal consultants, an additional factor must be considered: overhead costs. These costs are allocated across all internal consultants and subsequently distributed to clients based on each consultant's performance.<br /><br />
              In practice, the process works as follows:<br /><br />
              The total overhead cost is determined.<br />
              This total is equally distributed among the internal consultants.<br />
              The allocated overhead per consultant is then split pro rata across the clients the consultant worked for, based on the consultant's performance for each client.<br /><br />
              <strong>Overhead costs include:</strong><br />
              Salaries for the management, more specific the Bulgarian and Spanish salaries for the CEO and COO<br />
              IT-related expenses in Belgium, Bulgaria and Spain, including internet and mobile services<br />
              Rent expenses across the three different countries where the company operates<br />
              Accounting services provided by the three accountants<br />
              Marketing and promotional expenses<br />
              IT-related costs and infrastructure<br />
              Bank charges and transaction fees<br />
              Subscriptions and software licenses<br />
              The management fee paid by Orka BE to Kamalotte BV, the Belgian holding company<br /><br />
              <strong>Overhead costs do not include</strong> the dividends distributed annually to Kamalotte from Orka BG and Orka SP.<br /><br />
              All rates and costs change overtime, working with from-to.
            </div>
          )}
        </div>

        {/* Grouped by SOURCE, so all three blue app buttons sit together and
            all three file uploads sit together. Grouping by kind of data
            instead put a blue button next to a green one inside each box,
            and at the moment of clicking that is what confuses: the hand
            goes to the colour, not to the caption. The group label carries
            the behaviour that actually differs — the apps MERGE per person,
            a workbook REPLACES its whole slot — which is also the reason
            the workbooks go first when both are used. Each button then says
            what it carries, because "Internals" means a different thing on
            either side. */}
        <div className="dash-header__actions">
          <div className="dash-group">
            <span className="dash-group__label">
              From the apps <span className="dash-group__note">· merge</span>
            </span>
            <div className="dash-group__row">
              <button
                className="dash-btn dash-btn--primary"
                onClick={() => setPullChronaOpen(true)}
                disabled={loading || !hasChronaToken}
                title={
                  hasChronaToken
                    ? "Hours, from Chrona, for a date range. Replaces the hours slot it loads (internals or externals)."
                    : "Log out and log in again to enable the Chrona pull"
                }
              >
                Chrona · hours
              </button>
              <button
                className="dash-btn dash-btn--primary"
                onClick={handlePullSourcixRates}
                disabled={loading}
                title="Externals: buying cost + selling rate, from Sourcix. Merges — replaces only the people it carries, everyone else is kept."
              >
                Sourcix · externals
              </button>
              <button
                className="dash-btn dash-btn--primary"
                onClick={handlePullTalexaCosts}
                disabled={loading}
                title="Internals: cost per hour, selling rates and the company overhead, from Talexa. Merges — replaces only the people it carries."
              >
                Talexa · internals
              </button>
            </div>
          </div>

          <div className="dash-group">
            <span className="dash-group__label">
              From Excel <span className="dash-group__note">· replace</span>
            </span>
            <div className="dash-group__row">
              <button
                className={`dash-btn ${internalsOk ? "dash-btn--ok" : ""}`}
                onClick={() => document.getElementById("excel-internals")?.click()}
                disabled={loading || internalsOk}
                title={
                  internalsOk
                    ? "Internals hours are loaded. Reset to load a different file."
                    : "Upload the internals hours workbook (Tempo export)"
                }
              >
                {internalsOk ? "Internals hours ✓" : "Internals hours"}
              </button>
              <button
                className={`dash-btn ${externalsOk ? "dash-btn--ok" : ""}`}
                onClick={() => document.getElementById("excel-externals")?.click()}
                disabled={loading || externalsOk}
                title={
                  externalsOk
                    ? "Externals hours are loaded. Reset to load a different file."
                    : "Upload the externals hours workbook (Tempo export)"
                }
              >
                {externalsOk ? "Externals hours ✓" : "Externals hours"}
              </button>
              <button
                className={`dash-btn ${ratesOk ? "dash-btn--ok" : ""}`}
                onClick={handleRatesExcelClick}
                // Deliberately NOT disabled once loaded, unlike the hours
                // buttons: the workbook is the only way to load a rate the
                // apps do not hold yet, and locking it would leave no route
                // back short of a full Reset. It REPLACES the whole rates
                // blob, so handleRatesExcelClick asks first once a pull is
                // in there.
                disabled={loading}
                title={
                  ratesOk
                    ? "Replace ALL loaded rates, costs and overhead with the 3-sheet workbook. Pull Sourcix and Talexa again afterwards."
                    : "Import the 3-sheet rates workbook"
                }
              >
                {ratesOk ? "Rates ✓" : "Rates"}
              </button>
            </div>
          </div>

          {/* When and from where the loaded data came. The dashboard could
              not tell an hour-old pull from a months-old one, and every
              "this doesn't add up" so far traced back to exactly that —
              a client that no longer exists, a consultant who left in 2023
              still showing. */}
          {(loadedAt.hours || loadedAt.rates) && (
            <div className="dash-loaded">
              {loadedAt.hours && (
                <span title={`Hours loaded ${loadedAt.hours.exact}`}>
                  Hours: {loadedAt.hours.source} · {loadedAt.hours.ago}
                </span>
              )}
              {loadedAt.rates && (
                <span title={`Rates loaded ${loadedAt.rates.exact}`}>
                  Rates: {loadedAt.rates.source} · {loadedAt.rates.ago}
                </span>
              )}
            </div>
          )}

          <button
            className="dash-btn dash-btn--reset"
            onClick={handleResetData}
            // Clearing while an import is in flight lets the response land
            // afterwards and re-persist what was just cleared — the pull
            // handlers even merge against the pre-reset blob they closed over.
            disabled={loading}
            title="Clear all loaded hours, rates and overhead from this browser"
          >
            Reset
          </button>
        </div>
      </div>

      <PullFromChronaModal
        open={pullChronaOpen}
        onClose={() => setPullChronaOpen(false)}
        onLoaded={handleChronaLoaded}
      />

      {/* ── INLINE UPLOAD ERROR (audit #46) ── */}
      {uploadError && (
        <div
          role="alert"
          style={{
            margin: "0 24px 16px",
            padding: "10px 14px",
            borderRadius: 8,
            background: "#fef2f2",
            border: "1px solid #fecaca",
            color: "#b91c1c",
            fontSize: 13,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <span>{uploadError}</span>
          <button
            onClick={() => setUploadError(null)}
            style={{
              background: "transparent",
              border: "none",
              color: "#b91c1c",
              cursor: "pointer",
              fontSize: 16,
              lineHeight: 1,
            }}
            aria-label="Dismiss error"
          >
            ×
          </button>
        </div>
      )}

      {/* ── PULL NOTICES ──
          Data problems the feeds report (a name that will not match, a
          leaver with no end date). Informative, not errors, so they get
          their own dismissible amber banner rather than the red one. */}
      <PullNotices
        title="Sourcix pull notes"
        notes={sourcixNotices}
        onDismiss={() => setSourcixNotices([])}
      />
      <PullNotices
        title="Talexa pull notes"
        notes={talexaNotices}
        onDismiss={() => setTalexaNotices([])}
      />

      {/* ── MISSING OVERHEAD WARNING ──
          Rates are loaded and internals hours exist, but no overhead rows:
          every internals margin is overstated by exactly the overhead of
          the period, and nothing else in the UI says so (the calc chain
          silently degrades to 0). Typical cause: pulling from Sourcix (which
          carries no overhead) without either importing the rates Excel or
          pulling from Talexa. */}
      {ratesOk && internalsOk && !(overhead && overhead.length) && (
        <div
          role="alert"
          style={{
            margin: "0 24px 16px",
            padding: "12px 16px",
            borderRadius: 8,
            background: "#fff8ec",
            border: "1px solid #f3d8a8",
            color: "#7a5a1e",
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          <b>⚠ No overhead loaded</b> — internals margins are currently
          overstated: gross margin ignores the company overhead entirely.
          Fix it with <b>Rates &amp; costs → Talexa</b>, or by importing the
          rates workbook (its Overhead sheet). A Sourcix pull alone carries
          no overhead — it only covers the externals.
        </div>
      )}

      {/* ── UNPRICED HOURS ──
          The chips only check that something is loaded, so a Talexa-only
          pull shows a green dashboard while every external hour earns €0,
          and a missing cost rate is worse still: it flatters the margin
          instead of shrinking it. Both figures are already in hand from the
          engine — the gap was that nothing added them up in one place.
          Percentages, because 40 h missing means something different on
          200 h than on 20,000. */}
      {(gaps.rates > 0 || gaps.costs > 0) && (
        <div
          role="alert"
          style={{
            margin: "0 24px 16px",
            padding: "12px 16px",
            borderRadius: 8,
            background: "#fff8ec",
            border: "1px solid #f3d8a8",
            color: "#7a5a1e",
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          <b>⚠ Hours without a rate in this period</b>
          <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
            {gaps.rates > 0 && (
              <li>
                <b>{fmtHours(gaps.rates)} h</b> ({pctOfHours(gaps.rates)}) have
                no <b>selling</b> rate — they earn €0, so revenue and margin
                are understated.
              </li>
            )}
            {gaps.costs > 0 && (
              <li>
                <b>{fmtHours(gaps.costs)} h</b> ({pctOfHours(gaps.costs)}) have
                no <b>cost</b> rate — they cost €0, so the margin is
                <b> overstated</b>.
              </li>
            )}
          </ul>
          <div style={{ marginTop: 6 }}>
            Open a client to see which consultant is missing which. Fix the
            rate in Sourcix (externals) or Talexa (internals) and pull again.
          </div>
        </div>
      )}

      {/* ── OVERLAPPING FIXED RATES WARNING ── */}
      {fixedOverlaps.length > 0 && (
        <div
          role="alert"
          style={{
            margin: "0 24px 16px",
            padding: "12px 16px",
            borderRadius: 8,
            background: "#fff8ec",
            border: "1px solid #f3d8a8",
            color: "#7a5a1e",
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 6 }}>
            ⚠ Overlapping fixed rates detected — a revenue figure may be wrong
          </div>
          <div style={{ marginBottom: 8 }}>
            Two fixed-rate rows apply to the same day for the consultant(s) below.
            The dashboard keeps the one with the latest start date, so an earlier
            period (e.g. part-time) can be hidden under a later one (e.g. full-time).
            This is almost always a wrong date in the rates Excel — check that
            consecutive periods don't overlap.
          </div>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {fixedOverlaps.map((o, i) => (
              <li key={i} style={{ marginBottom: 2 }}>
                <b>{o.user}</b> @ <b>{o.project}</b>: {o.a.from}→{o.a.to}{" "}
                ({o.a.monthly}/mo, {o.a.split}%) overlaps {o.b.from}→{o.b.to}{" "}
                ({o.b.monthly}/mo, {o.b.split}%)
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── EMPTY STATE: guide the user to import the three files (audit #48) ── */}
      {!importsOk && (
        <div
          className="dash-empty-state"
          style={{
            margin: "0 24px 24px",
            padding: "24px",
            borderRadius: 12,
            background: "#f8fafc",
            border: "1px dashed #cbd5e1",
            color: "#475569",
          }}
        >
          <h2 style={{ margin: "0 0 8px", fontSize: 18, color: "#0f172a" }}>
            Import your data to begin
          </h2>
          <p style={{ margin: "0 0 16px", fontSize: 13 }}>
            Load hours and rates to see the dashboard — from the apps
            (<b>Pull from Chrona</b>, then <b>Sourcix</b> and <b>Talexa</b>)
            or from the Excel workbooks. If you use both, import the
            workbooks <b>first</b>: they replace, the pulls merge on top.
          </p>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.9 }}>
            <li>{internalsOk ? "✓" : "○"} Internals hours</li>
            <li>{externalsOk ? "✓" : "○"} Externals hours</li>
            <li>{ratesOk ? "✓" : "○"} Rates (selling / buying / overhead)</li>
          </ul>
          {loading && (
            <p style={{ margin: "16px 0 0", fontSize: 13, color: "#0f172a" }}>
              Uploading…
            </p>
          )}
        </div>
      )}

      {/* ── KPI CARDS ── */}
      {importsOk && (
        <div className="dash-kpi-row">
          <div className="dash-kpi">
            <span className="dash-kpi__label">Total Revenue</span>
            <span className="dash-kpi__value">
              {(() => {
                const v = clientMargins.reduce((s, r) => s + (r.revenue || 0), 0);
                if (v >= 1_000_000) return `€${Math.round(v / 10_000) / 100}M`;
                if (v >= 1_000) return `€${Math.round(v / 100) / 10}k`;
                return `€${Math.round(v)}`;
              })()}
            </span>
          </div>
          <div className="dash-kpi">
            <span className="dash-kpi__label">Total Cost</span>
            <span className="dash-kpi__value">
              {(() => {
                const v = clientMargins.reduce((s, r) => s + (r.cost || 0), 0);
                if (v >= 1_000_000) return `€${Math.round(v / 100_000) / 10}M`;
                if (v >= 1_000) return `€${Math.round(v / 100) / 10}k`;
                return `€${Math.round(v)}`;
              })()}
            </span>
            <span className="dash-kpi__sub">Direct + overhead</span>
          </div>
          <div className="dash-kpi">
            <span className="dash-kpi__label">Gross Margin</span>
            <span className="dash-kpi__value">
              {(() => {
                const v = clientMargins.reduce((s, r) => s + (r.margin || 0), 0);
                if (v >= 1_000_000) return `€${Math.round(v / 10_000) / 100}M`;
                if (v >= 1_000) return `€${Math.round(v / 100) / 10}k`;
                return `€${Math.round(v)}`;
              })()}
            </span>
            {(() => {
              const rev = clientMargins.reduce((s, r) => s + (r.revenue || 0), 0);
              const mar = clientMargins.reduce((s, r) => s + (r.margin  || 0), 0);
              const pct = rev > 0 ? (mar / rev) * 100 : 0;
              return (
                <span className={`dash-kpi__sub ${pct >= 30 ? "dash-kpi__sub--up" : "dash-kpi__sub--down"}`}>
                  {pct.toFixed(1)}%
                </span>
              );
            })()}
          </div>
          <div className="dash-kpi">
            <span className="dash-kpi__label">Allocated overhead</span>
            <span className="dash-kpi__value">
              {(() => {
                const v = clientMargins.reduce((s, r) => s + (r.overhead || 0), 0);
                if (v >= 1_000_000) return `€${(v / 1_000_000).toFixed(1)}M`;
                if (v >= 1_000) return `€${(v / 1_000).toFixed(1)}k`;
                return `€${v.toFixed(0)}`;
              })()}
            </span>
          </div>
          <div className="dash-kpi">
            <span className="dash-kpi__label">Active Clients</span>
            <span className="dash-kpi__value">{clientMargins.length}</span>
          </div>
        </div>
      )}

      {importsOk && (
        <div style={{ padding:24 }}>
          <TimeNavigator
            view={view}
            setView={setView}
            year={year}
            setYear={setYear}
            periodIndex={periodIndex}
            setPeriodIndex={setPeriodIndex}
          />
        </div>
      )}

      {importsOk && (
        <div className="fade-in" style={{
          display:"grid",
          gridTemplateColumns:"1fr 1fr",
          gap:32,
          marginBottom:24
        }}>

          <div className="fade-in">


            <ClientTreemap
              data={treemapData}
              selectedClient={selectedClient}
              onClientClick={setSelectedClient}
              onBack={() => setSelectedClient(null)}
              hoveredItem={hoveredItem}
              setHoveredItem={setHoveredItem}
            />

          </div>

          <ClientTreemapTable
            data={treemapTableData}
            hoveredItem={hoveredItem}
            setHoveredItem={setHoveredItem}
          />

        </div>
      )}

      {importsOk && (
        <PerformancePanel data={clientMargins} />
      )}

      <ConsultantClientTable data={consultantClientRows} />

      {/* The three hidden file inputs CLEAR their value after handling the
          selection. A file input only fires `change` when the value
          actually changes, so without this, picking the SAME file again is
          a silent no-op: the user fixes a date in rates.xlsx, re-imports
          it, and the dashboard quietly keeps the old numbers. */}
      <input id="excel-internals" type="file" hidden
        onChange={async e=>{
          const file=e.target.files?.[0];
          e.target.value="";
          if(!file)return;
          await uploadExcel({
            file,
            endpoint:`${API_BASE_URL}/excel/import/internal`,
            onSuccess:setInternals,
            storageKey:STORAGE_KEYS.internals,
            source:"Excel"
          });
        }}
      />

      <input id="excel-externals" type="file" hidden
        onChange={async e=>{
          const file=e.target.files?.[0];
          e.target.value="";
          if(!file)return;
          await uploadExcel({
            file,
            endpoint:`${API_BASE_URL}/excel/import/external`,
            onSuccess:setExternals,
            storageKey:STORAGE_KEYS.externals,
            source:"Excel"
          });
        }}
      />

      <input id="excel-rates" type="file" hidden
        onChange={async e=>{
          const file=e.target.files?.[0];
          e.target.value="";
          if(!file)return;
          await uploadExcel({
            file,
            endpoint:`${API_BASE_URL}/excel/import/rates`,
            // A fresh workbook replaces the rates wholesale, so notes from
            // EITHER pull no longer describe the loaded data. Clearing only
            // Sourcix's left Talexa's notes hanging over data they had
            // nothing to do with.
            onSuccess:(data)=>{
              setRates(data);
              setSourcixNotices([]);
              setTalexaNotices([]);
            },
            storageKey:STORAGE_KEYS.rates,
            source:"Excel"
          });
        }}
      />

    </div>
  );

}